/**
 * Modèles pour l'onboarding AI (FlashLevel)
 * Remplace le niveau autodéclaré par un système de skills + perceptions
 */

/**
 * Compétence linguistique évaluable
 */
export interface Skill {
    id: string;
    name: string;
    icon: string;
    description: string;
}

/**
 * Niveau de perception autodéclaré par skill (1-3 étoiles)
 */
export interface PerceptionLevel {
    level: number;
    label: string;
    description: string;
}

/**
 * Réponse de perception pour une skill spécifique
 */
export interface PerceptionResponse {
    skillId: string;
    level: number;  // 1, 2 ou 3
}

/**
 * State complet de l'onboarding AI
 * Persiste dans localStorage via AIOnboardingStateService
 */
export interface AIOnboardingState {
    language?: string;
    skills?: string[];
    perceptions?: PerceptionResponse[];
    profileData?: {
        firstName: string;
        lastName: string;
        email: string;
        age: Date;
        gender: string;
        duration: number;
    };
    jobData?: {
        business: string;
        job: string;
    };
    companyData?: {
        company: string;
        company_details: string;
    };
}

/**
 * Skills disponibles pour l'évaluation
 * Mapping des 4 compétences CECRL
 */
export const AVAILABLE_SKILLS: Skill[] = [
    {
        id: 'listening',
        name: 'ai-exam-onboarding.skills.listening.name',
        icon: 'volume_up',
        description: 'ai-exam-onboarding.skills.listening.description'
    },
    {
        id: 'speaking',
        name: 'ai-exam-onboarding.skills.speaking.name',
        icon: 'mic',
        description: 'ai-exam-onboarding.skills.speaking.description'
    },
    {
        id: 'reading',
        name: 'ai-exam-onboarding.skills.reading.name',
        icon: 'menu_book',
        description: 'ai-exam-onboarding.skills.reading.description'
    },
    {
        id: 'writing',
        name: 'ai-exam-onboarding.skills.writing.name',
        icon: 'edit',
        description: 'ai-exam-onboarding.skills.writing.description'
    }
];

/**
 * Retourne la liste des skills autorisées pour un Elate.
 *
 * Règle: si elate.elate_rec.allowed_elate_axes est défini et non vide,
 * filtre AVAILABLE_SKILLS sur cette whitelist. Sinon retourne toutes les skills
 * (rétrocompatibilité).
 *
 * Le type Elate n'est pas importé pour éviter une dépendance circulaire :
 * on accepte un objet minimal exposant elate_rec.allowed_elate_axes.
 */
export function getAllowedSkills(
    elate?: { elate_rec?: { allowed_elate_axes?: string[] | null } } | null,
): Skill[] {
    const allowed = elate?.elate_rec?.allowed_elate_axes;
    if (!allowed || allowed.length === 0) {
        return AVAILABLE_SKILLS;
    }
    return AVAILABLE_SKILLS.filter((skill) => allowed.includes(skill.id));
}

/**
 * Niveaux de perception (3 étoiles)
 * Utilisés dans les étapes dynamiques par skill
 */
export const PERCEPTION_LEVELS: PerceptionLevel[] = [
    {
        level: 3,
        label: 'ai-exam-onboarding.perception-level.3.label',
        description: 'ai-exam-onboarding.perception-level.3.description'
    },
    {
        level: 2,
        label: 'ai-exam-onboarding.perception-level.2.label',
        description: 'ai-exam-onboarding.perception-level.2.description'
    },
    {
        level: 1,
        label: 'ai-exam-onboarding.perception-level.1.label',
        description: 'ai-exam-onboarding.perception-level.1.description'
    }
];

/**
 * Type de compétence linguistique
 * - comprehension : nécessite un article (le/l') en FR — Comprendre, Lire
 * - production : utilise "en" en FR — Parler, Écrire
 */
export type SkillType = 'comprehension' | 'production';

/**
 * Mapping des verbes d'action par compétence
 * Utilisé pour générer dynamiquement les phrases dans les étapes perception
 * Le connecteur (article/préposition) est calculé dynamiquement selon la locale
 * et la première lettre du nom de langue (élision française, etc.)
 */
export const SKILL_ACTION_VERBS: { [key: string]: { verb: string; type: SkillType } } = {
    'listening': { verb: 'ai-exam-onboarding.perception-step.action.listening.verb', type: 'comprehension' },
    'speaking': { verb: 'ai-exam-onboarding.perception-step.action.speaking.verb', type: 'production' },
    'reading': { verb: 'ai-exam-onboarding.perception-step.action.reading.verb', type: 'comprehension' },
    'writing': { verb: 'ai-exam-onboarding.perception-step.action.writing.verb', type: 'production' }
};

/**
 * Descriptions adaptées par skill pour les niveaux de perception
 * Format : { skillId: { level: description } }
 */
export const SKILL_PERCEPTION_DESCRIPTIONS: { [skillId: string]: { [level: number]: string } } = {
    'listening': {
        3: 'ai-exam-onboarding.perception.listening.level-3',
        2: 'ai-exam-onboarding.perception.listening.level-2',
        1: 'ai-exam-onboarding.perception.listening.level-1'
    },
    'speaking': {
        3: 'ai-exam-onboarding.perception.speaking.level-3',
        2: 'ai-exam-onboarding.perception.speaking.level-2',
        1: 'ai-exam-onboarding.perception.speaking.level-1'
    },
    'reading': {
        3: 'ai-exam-onboarding.perception.reading.level-3',
        2: 'ai-exam-onboarding.perception.reading.level-2',
        1: 'ai-exam-onboarding.perception.reading.level-1'
    },
    'writing': {
        3: 'ai-exam-onboarding.perception.writing.level-3',
        2: 'ai-exam-onboarding.perception.writing.level-2',
        1: 'ai-exam-onboarding.perception.writing.level-1'
    }
};

/**
 * Libellés des compétences sur l'écran jauges de perception (maquette 3).
 */
export const PERCEPTION_GAUGE_SKILL_LABELS: Record<string, string> = {
    listening: 'ai-exam-onboarding.perception-gauges.skill.listening',
    speaking: 'ai-exam-onboarding.perception-gauges.skill.speaking',
    reading: 'ai-exam-onboarding.perception-gauges.skill.reading',
    writing: 'ai-exam-onboarding.perception-gauges.skill.writing',
};

/** Emojis compétence pour l'écran jauges (style chips comfort). */
export const PERCEPTION_GAUGE_SKILL_ICONS: Record<string, string> = {
    listening: '👂',
    speaking: '🎙️',
    reading: '📖',
    writing: '✍️',
};
